import { createContext, useCallback, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import type { JSX, ReactNode } from 'react';

import { getFragmentData } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';

import { LoadingFailure } from '../../components/LoadingFailure';
import { getLoadingFailureMessage } from '../../utils/loading-failure-message.util';
import { useIdParam } from '../../utils/use-id-param.util';
import { useFindOneReportQuery } from '../../pages/MissionReport/hooks/useFindOneReportQuery.hook';
import { MissionReportPage_ReportInfo_Fragment } from '../../pages/MissionReport/fragments/report-info.fragment';

import { useMissionContext } from '../MissionContext/hooks/useMissionContext.hook';
import { MissionContextStatus } from '../MissionContext/types/mission-context.type';

import { ReportContextStatus } from './types/report-context.type';
import type { ReportContextData } from './types/report-context.type';

const ReportContext = createContext<ReportContextData | null>(null);

interface Props {
  readonly children?: ReactNode;
}

const ReportContextProvider = ({ children }: Readonly<Props>): JSX.Element => {
  const { status: missionStatus, mission } = useMissionContext();

  const reportInfoId = useIdParam('reportInfoId');

  const missionReportInfo = getFragmentData(
    MissionReportPage_ReportInfo_Fragment,
    mission?.reportInfos,
  )?.find((reportInfo) => reportInfo._id === reportInfoId);

  const missionReportId = missionReportInfo?.reportId ?? EMPTY_ID;

  const {
    data: reportData,
    isLoading: isReportLoading,
    isError: isReportError,
    error: reportError,
    refetch: refetchReport,
  } = useFindOneReportQuery(missionReportId);
  const missionReport = reportData?.findOneReport;

  const retry = useCallback(() => {
    void refetchReport();
  }, [refetchReport]);

  const failureMessage = getLoadingFailureMessage({
    isLoading: isReportLoading,
    isError: isReportError,
    error: reportError,
    isFound: !missionReportInfo?.reportId || !!missionReport,
    subject: 'le rapport',
    notFoundMessage: 'Ce rapport est introuvable.',
  });

  const contextValue = useMemo<ReportContextData>(() => {
    // If mission is still loading, report context is also loading
    if (missionStatus === MissionContextStatus.Loading || isReportLoading) {
      return {
        status: ReportContextStatus.Loading,
        missionReportId: undefined,
        missionReport: undefined,
        missionReportInfo: undefined,
      };
    }

    // If mission has error, report context also has error
    if (missionStatus === MissionContextStatus.Error || isReportError) {
      return {
        status: ReportContextStatus.Error,
        missionReportId: undefined,
        missionReport: undefined,
        missionReportInfo: undefined,
      };
    }

    // If no report ID, report is not initialized
    if (!missionReportInfo?.reportId) {
      return {
        status: ReportContextStatus.NotInitialized,
        missionReportId: undefined,
        missionReport: undefined,
        missionReportInfo: undefined,
      };
    }

    // The mission references a report that could not be found
    if (!missionReport) {
      return {
        status: ReportContextStatus.Error,
        missionReportId: undefined,
        missionReport: undefined,
        missionReportInfo: undefined,
      };
    }

    return {
      status: ReportContextStatus.Ready,
      missionReportId: missionReportInfo.reportId,
      missionReport,
      missionReportInfo,
    };
  }, [
    missionStatus,
    isReportLoading,
    isReportError,
    missionReport,
    missionReportInfo,
  ]);

  return (
    <ReportContext.Provider value={contextValue}>
      {failureMessage ? (
        <LoadingFailure failure={failureMessage} onRetry={retry} />
      ) : (
        (children ?? <Outlet />)
      )}
    </ReportContext.Provider>
  );
};

export { ReportContext, ReportContextProvider };
